/* eslint-disable @typescript-eslint/ban-types */
import { useConfig, useNavigation, useOidc, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Location, VoiceApi } from '../../services/api-client/voice-api';
import { EventActiveLocation, EventLocationChanged, EventLocationSwitchComplete } from '../../types/event-bus';
import { useEventBus } from '../use-event-bus';
import { ChangeLocationStatus } from './types/change-location-status';

export interface LocationsCtx {
  locations: Location[];
  selectedLocationIds: number[];
  selectedLocation: Location | undefined;
  defaultLocationId: number | null;
  setLocationIds: (locationIds: number[]) => void;
  selectedLocationWithStatus: SelectedLocation | undefined;
  updateSelectedLocationWithStatus: (value: Partial<SelectedLocation>) => void;
  switchLocation: (id: number) => Promise<void>;
}

export interface SelectedLocation {
  location: Location;
  status: ChangeLocationStatus;
}

export const LocationsContext = createContext<LocationsCtx>(null!);

const LocationsProvider = ({ children }: PropsWithChildren<{}>) => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { getClaims } = useOidc();
  const { publishToIframes, publish, listen } = useEventBus();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [selectedLocationWithStatus, setSelectedLocationWithStatus] = useState<SelectedLocation>();
  const [defaultLocationId, setDefaultLocationId] = useState<number | null>(null);
  const { getLocationIdUrlParam } = useNavigation();
  const history = useHistory();
  const query = new URLSearchParams(history.location.search);
  const queryLocationId = Number(query.get('locationId') || 0) || null;

  const setLocationIds = useCallback((locationIds: number[]) => {
    setSelectedLocationIds(locationIds.map((id) => Number(id)));
  }, []);

  useEffect(() => {
    (async () => {
      const claims = await getClaims();

      setDefaultLocationId(Number(queryLocationId || getLocationIdUrlParam() || claims.locationId));
    })();
  }, []);

  useEffect(() => {
    if (defaultLocationId && selectedLocationIds.length === 0) {
      setLocationIds([defaultLocationId]);
    }
  }, [defaultLocationId, selectedLocationIds]);

  useEffect(() => {
    if (queryLocationId && defaultLocationId !== queryLocationId && selectedLocationIds[0] !== queryLocationId) {
      setLocationIds([queryLocationId]);
    }
  }, [queryLocationId]);

  useEffect(() => {
    if (!(config && oidcService)) return;

    (async () => {
      const result = await new VoiceApi(config, oidcService).getLocations();

      setLocations(result);
    })();
  }, [config, oidcService]);

  useEffect(() => {
    if (!selectedLocationWithStatus) return;

    if (selectedLocationWithStatus.location && selectedLocationWithStatus.status == 'Complete') {
      publish({
        messageType: 'location-switch-complete',
        data: { locationId: Number(selectedLocationWithStatus.location.id) }
      });
    }
  }, [selectedLocationWithStatus]);

  const updateSelectedLocationWithStatus = (value: Partial<SelectedLocation>) => {
    setSelectedLocationWithStatus((x) => (x ? { ...x, ...value } : undefined));
  };

  // returns promise when location switch is complete
  const switchLocation = async (id: number) => {
    setLocationIds([id]);
    publish({ messageType: 'location-switch-start', data: { locationId: id } });

    await new Promise((resolve) => {
      const unsubscribe = listen('location-switch-complete', (event) => {
        const eventLocationId = (event as EventLocationSwitchComplete).data.locationId;

        if (Number(eventLocationId) === Number(id)) {
          unsubscribe();
          resolve(undefined);
        }
      });
    });
  };

  const selectedLocation = useMemo(
    () => locations.find((location) => Number(location.id) === Number(selectedLocationIds[0])),
    [locations, selectedLocationIds]
  );

  const selectedLocationId = Number(selectedLocation?.id);

  useEffect(() => {
    if (selectedLocation) {
      setSelectedLocationWithStatus({ location: selectedLocation, status: 'InProgress' });

      const message: EventLocationChanged = {
        messageType: 'locationChanged',
        data: {
          locationId: selectedLocation.id,
          name: selectedLocation.name
        }
      };

      publishToIframes(message);
      publish(message);
    }
  }, [selectedLocationId]);

  useEffect(
    () =>
      listen('getActiveLocation', () => {
        if (selectedLocation) {
          const message: EventActiveLocation = {
            messageType: 'activeLocation',
            data: {
              locationId: selectedLocation.id,
              name: selectedLocation.name
            }
          };

          publishToIframes(message);
          publish(message);
        }
      }),
    [selectedLocationId]
  );

  return (
    <LocationsContext.Provider
      value={{
        locations,
        selectedLocationIds,
        setLocationIds,
        selectedLocation,
        selectedLocationWithStatus,
        defaultLocationId,
        updateSelectedLocationWithStatus,
        switchLocation
      }}
    >
      {children}
    </LocationsContext.Provider>
  );
};

export default LocationsProvider;
